export interface ProcessStep {
  title: string
  text: string
}

export interface FaqItem {
  q: string
  a: string
}

export interface OfferPackage {
  slug: string
  category: 'Divorț & Partaj' | 'Penal' | 'Moșteniri' | 'Executări'
  badge: string
  image: string
  shortDescription: string
  hero: {
    title: string
    subtitle: string
  }
  awareness: {
    title: string
    paragraphs: string[]
  }
  benefits: {
    title: string
    items: string[]
  }
  howWeHelp: {
    title: string
    intro: string
    items: string[]
  }
  process: {
    title: string
    steps: ProcessStep[]
  }
  forWhom: {
    forTitle: string
    forItems: string[]
    notForTitle: string
    notForItems: string[]
  }
  included: {
    title: string
    items: string[]
    ctaText: string
    ctaSmall: string
  }
  testimonials: {
    title: string
  }
  faq: {
    title: string
    items: FaqItem[]
  }
}

const CTA_SMALL =
  'Sesiunea de 150 EUR + TVA se aplică integral la pachet dacă decidem să continuăm împreună.'

export const PACKAGES: Record<string, OfferPackage> = {
  divort: {
    slug: 'divort',
    category: 'Divorț & Partaj',
    badge: 'DIVORȚ',
    image:
      'https://images.unsplash.com/photo-1505664194779-8beaceb93744?w=800&h=600&fit=crop',
    shortDescription:
      'Desfaci căsătoria în cel mai scurt timp posibil, cu claritate deplină la fiecare pas.',
    hero: {
      title: 'Pachetul de Divorț',
      subtitle:
        'Desfaci căsătoria în cel mai scurt timp posibil, cu claritate deplină la fiecare pas, fără să fie nevoie să vii la birou sau la instanță.',
    },
    awareness: {
      title: 'Știi că vrei să divorțezi. Dar nu știi de unde să începi.',
      paragraphs: [
        'Ești într-o căsătorie care s-a terminat, dar procesul de divorț ți se pare complicat, lung și plin de necunoscute. Nu știi ce se întâmplă cu copiii, cine rămâne în casă, cât durează, cât costă și ce acte trebuie să pregătești.',
        'Poate locuiești în altă țară și îți pare imposibil să rezolvi asta de la distanță. Poate soțul tău spune că nu va fi niciodată de acord cu divorțul. Poate ți-e frică că vei pierde copiii sau că judecătorul nu te va asculta.',
        'Toate acestea sunt întrebări cu răspunsuri clare. Divorțul se poate face indiferent dacă ești în România sau în străinătate, indiferent dacă soțul este sau nu de acord, cu transparență completă la fiecare etapă.',
      ],
    },
    benefits: {
      title: 'Ce obții lucrând cu mine pe dosarul de divorț',
      items: [
        'Poți divorța de oriunde te afli, fără să vii la birou sau la instanță. Contractul se semnează online, actele se trimit prin email sau WhatsApp și totul este perfect legal.',
        'Știi în orice moment unde se află dosarul tău. Îți comunic numărul dosarului, actele depuse, probele administrate și ce a depus cealaltă parte, fără să trebuiești să întrebi tu.',
        'Stabilim împreună strategia pentru custodie, program de vizită și pensie de întreținere, în funcție de ce îți dorești tu concret, nu un răspuns standard aplicat tuturor.',
        'Putem cere instanței să stabilească care dintre soți folosește locuința familiei după divorț, chiar dacă nu ai dosar de partaj deschis.',
        'Formăm o echipă. Înainte de fiecare termen pregătim împreună probele și argumentele, astfel încât instanța să primească cele mai bune variante pentru cazul tău.',
        'Dacă există violențe sau adulter dovedit, putem cere divorțul din culpa exclusivă a celuilalt soț, ceea ce schimbă semnificativ rezultatul final.',
      ],
    },
    howWeHelp: {
      title: 'Ce clarificăm împreună înainte de a porni dosarul',
      intro:
        'Divorțul este unul dintre cele mai dificile procese prin care trece o persoană, nu doar din punct de vedere juridic, ci și emoțional. Înainte de a porni orice dosar, îți explicăm exact unde ești din punct de vedere legal, ce poți obține și care este cel mai bun drum pentru situația ta. Nu primești un răspuns standard. Primești o analiză a cazului tău.',
      items: [
        'Procedura de divorț prin instanță, pas cu pas, pe înțelesul tău',
        'Ce probe trebuie strânse și cum le administrăm în timpul procesului',
        'Taxa de timbru pentru divorț și cât costă în realitate',
        'La ce judecătorie se introduce cererea și de ce contează asta',
        'Ce soluții poate pronunța instanța și la ce să te aștepți concret',
        'Cât durează un proces de divorț în funcție de situația ta specifică',
        'Cine poate veni ca martor și cum pregătim martorii',
        'Ce se întâmplă dacă celălalt soț nu este de acord cu divorțul',
        'Dacă poți divorța fără să faci partaj în același timp',
        'Ce acte sunt necesare pentru depunerea dosarului',
      ],
    },
    process: {
      title: 'Cum lucrăm împreună de la primul pas până la final',
      steps: [
        {
          title: 'Pasul 1 — Sesiunea de Claritate',
          text: 'Înainte de orice, îmi scrii pe email situația ta: de ce vrei divorțul, ce îți dorești să obții și cum se poziționează celălalt soț față de acest demers. În sesiunea inițială înțeleg cazul tău în detaliu și îți explic exact ce poți obține. Primești răspunsuri clare la toate întrebările de mai sus înainte de a semna orice.',
        },
        {
          title: 'Pasul 2 — Redactarea și depunerea dosarului',
          text: 'Redactez acțiunea de divorț cu toate cererile accesorii: custodia copiilor, programul de vizită, pensia de întreținere și atribuirea locuinței. Îți trimit documentul înainte de depunere, ca să fii confortabil cu fiecare cuvânt scris acolo.',
        },
        {
          title: 'Pasul 3 — Procedura prealabilă',
          text: 'Dosarul este comunicat celeilalte părți, care are 25 de zile să depună propriile cereri. Răspundem împreună la tot ce depune, cu argumente și probe pregătite.',
        },
        {
          title: 'Pasul 4 — Reprezentarea în instanță',
          text: 'Mă prezint la fiecare termen de judecată în numele tău. Ești informat după fiecare termen despre ce s-a întâmplat și ce urmează. Poți fi și tu prezent dacă dorești, dar nu este obligatoriu.',
        },
      ],
    },
    forWhom: {
      forTitle: 'Pachetul de Divorț este pentru tine dacă',
      forItems: [
        'Știi că vrei să divorțezi și ești gata să începi procesul acum, nu peste șase luni, și vrei să înțelegi exact ce drepturi ai legat de copii, casă și bunuri înainte de orice decizie.',
        'Locuiești în România sau în altă țară și vrei să rezolvi totul fără deplasări inutile, fără să îți iei concediu și fără să cheltui bani pe drumuri.',
        'Vrei un avocat care să îți explice fiecare pas și să fii informat în orice moment, nu să aștepți să afli tu ce s-a întâmplat la termen.',
      ],
      notForTitle: 'Pachetul de Divorț nu este pentru tine dacă',
      notForItems: [
        'Nu ești hotărât dacă vrei divorțul și cauți pe cineva care să decidă în locul tău sau care să îți confirme că te vei răzgândi.',
        'Vrei să consulți mai mulți avocați în paralel, să compari opinii contradictorii și să nu urmezi niciun plan clar până la urmă.',
        'Crezi că poți obține orice îți dorești indiferent de probe și de situația reală, și nu ești dispus să asculți ce este realizabil în instanță pentru cazul tău.',
      ],
    },
    included: {
      title: 'Hai să vedem ce include Pachetul de Divorț',
      items: [
        'Sesiunea inițială de consultanță și analiza situației tale (150 EUR + TVA, aplicați integral la pachet dacă decidem să continuăm)',
        'Clarificări complete despre procedură, taxe, probe, termene și judecătorie înainte de a porni orice dosar',
        'Redactarea acțiunii de divorț cu toate cererile accesorii: custodie, pensie de întreținere, program de vizită și atribuirea locuinței',
        'Comunicarea și colaborarea cu tine înainte de depunerea dosarului',
        'Trimiterea dosarului la instanță și urmărirea procedurii prealabile',
        'Răspuns scris la tot ce depune cealaltă parte',
        'Pregătirea fiecărui termen de judecată împreună cu tine',
        'Reprezentare la fiecare termen de judecată',
        'Comunicare permanentă pe email și WhatsApp pe tot parcursul dosarului',
      ],
      ctaText: 'Rezervă Sesiunea de Claritate și află prețul pachetului',
      ctaSmall: CTA_SMALL,
    },
    testimonials: {
      title: 'Ce spun clienții care au trecut prin dosarul de divorț cu mine',
    },
    faq: {
      title: 'Întrebări frecvente despre Pachetul de Divorț',
      items: [
        {
          q: 'Pot divorța dacă soțul meu nu este de acord?',
          a: 'Da. Nu ai nevoie de acordul lui ca să introduci cererea de divorț. Instanța analizează cererea ta independent. Soțul tău are dreptul să răspundă cu propriile cereri, dar lipsa acordului lui nu blochează și nu oprește procesul.',
        },
        {
          q: 'Trebuie să vin la birou sau la instanță?',
          a: 'Nu este necesar. Contractul se semnează electronic, actele se trimit prin email sau WhatsApp, iar eu te reprezint la toate termenele. Poți fi prezent la instanță dacă dorești, dar nu este obligatoriu.',
        },
        {
          q: 'Ce se întâmplă cu copiii?',
          a: 'În acțiunea de divorț includem cererile pentru custodie, program de vizită și pensie de întreținere. Stabilim împreună varianta pe care o dorești, eu îți spun ce este realizabil și ce are cele mai mari șanse în fața judecătorului.',
        },
        {
          q: 'Pot obține custodia exclusivă a copiilor?',
          a: 'Da, în anumite condiții. Legea cere dovezi clare că celălalt părinte împiedică exercitarea autorității părintești sau că există violențe, refuz de îngrijiri medicale sau comportamente care dăunează copilului. Analizăm situația ta în detaliu în sesiunea de consultanță.',
        },
        {
          q: 'Cât durează un dosar de divorț?',
          a: 'Depinde de complexitatea situației și de instanța la care se judecă dosarul. Un divorț prin acord comun poate fi mai rapid. Un divorț cu cereri accesorii pentru custodie, pensie și program de vizită durează mai mult. Important este că, odată depus dosarul, procesul merge înainte indiferent de atitudinea celeilalte părți.',
        },
        {
          q: 'Pot cere să rămân în casă până la soluționarea partajului?',
          a: 'Da. În cadrul dosarului de divorț sau printr-o ordonanță președințială separată, adică o procedură urgentă prin care instanța ia o decizie rapidă, se poate stabili care dintre soți folosește locuința familiei. Nu ai nevoie de un dosar de partaj deschis pentru asta.',
        },
        {
          q: 'Cum se stabilește pensia de întreținere?',
          a: 'Pensia se stabilește fie ca sumă fixă de bani, fie ca procent din venitul părintelui care o plătește. Suma fixă se mărește automat în fiecare an cu rata inflației. Procentul depinde de veniturile dovedite cu acte ale celuilalt părinte. Discutăm în sesiunea de consultanță care variantă este mai avantajoasă pentru situația ta concretă.',
        },
      ],
    },
  },

  partaj: {
    slug: 'partaj',
    category: 'Divorț & Partaj',
    badge: 'PARTAJ',
    image:
      'https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&h=600&fit=crop',
    shortDescription:
      'Îți recuperezi dreptul de proprietate asupra bunurilor din căsătorie, cu costuri clare de la început.',
    hero: {
      title: 'Pachetul de Partaj',
      subtitle:
        'Îți recuperezi dreptul de proprietate asupra bunurilor din căsătorie, cu costuri clare de la început și fără surprize pe parcurs.',
    },
    awareness: {
      title:
        'Ai bunuri comune cu fostul soț și nu poți face nimic cu ele. Asta nu e normal.',
      paragraphs: [
        'Căsătoria s-a terminat, dar proprietățile sunt în continuare pe amândoi. Nu poți vinde, nu poți lua credit, nu poți decide nimic singur despre casa, mașina sau terenul pe care le-ați cumpărat împreună.',
        'Poate fostul soț refuză să negocieze sau cere o cotă care nu reflectă realitatea. Poate ai contribuit mai mult la achiziționarea bunurilor, dar pe hârtie sunt trecute pe el. Poate ai plecat din casă și acum el o folosește singur, în timp ce tu plătești chirie în altă parte.',
        'Partajul este procesul legal prin care îți recuperezi exact ce ți se cuvine. Și poate fi declanșat oricând, cu sau fără acordul fostului soț.',
      ],
    },
    benefits: {
      title: 'Ce obții lucrând cu mine pe dosarul de partaj',
      items: [
        'Claritate completă despre ce bunuri intră în partaj și care sunt drepturile tale reale, înainte de a merge în instanță.',
        'Îți recuperezi dreptul de proprietate și poți decide ce faci cu bunurile tale: le vinzi, le păstrezi sau le transmiți mai departe.',
        'Taxa de timbru pentru partaj a fost redusă cu 50% de stat. Dacă există dovezi de violență domestică, poți fi scutit complet de taxa de timbru.',
        'Dacă ai contribuit mai mult la achiziționarea bunurilor din căsătorie, poți obține o cotă mai mare de 50%, chiar și de 90%, cu probele corecte.',
        'Datoriile și creditele contractate în timpul căsătoriei se împart și ele. Mă ocup și de această parte, nu doar de bunuri.',
        'Nu ai nevoie de acordul fostului soț ca să introduci cererea de partaj. Odată depusă, instanța soluționează independent, fără să depinzi de bunăvoința celeilalte persoane.',
      ],
    },
    howWeHelp: {
      title: 'Ce clarificăm împreună înainte de a porni dosarul',
      intro:
        'Partajul este unul dintre subiectele despre care circulă cele mai multe informații greșite. Clienții vin adesea cu convingerea că taxa este uriașă, că nu pot obține mai mult de jumătate sau că fără acordul fostului soț nu se poate face nimic. Înainte de orice dosar, îți explicăm exact cum stau lucrurile în realitate, pentru situația ta concretă.',
      items: [
        'Ce bunuri intră în partaj și care sunt cele care nu se împart',
        'Cum se calculează taxa de timbru pentru partaj și cât costă în realitate',
        'Dacă poți obține mai mult de 50% din bunuri și în ce condiții',
        'Ce se întâmplă cu datoriile și creditele contractate în timpul căsătoriei',
        'Dacă ai nevoie de acordul fostului soț ca să introduci cererea',
        'Ce acte sunt necesare și ce faci dacă nu le ai pe toate',
        'Ce este expertiza bunurilor și cum te afectează în dosar',
        'Dacă este mai avantajos partajul la notar sau la instanță',
        'Cât durează un dosar de partaj și ce termene sunt de așteptat',
        'Ce se întâmplă dacă fostul soț cedează în timpul procesului',
      ],
    },
    process: {
      title: 'Cum lucrăm împreună de la primul pas până la final',
      steps: [
        {
          title: 'Pasul 1 — Sesiunea de Claritate',
          text: 'Îmi prezinți situația ta: ce bunuri ai, ce acte există și ce vrei să obții. Nu ai nevoie de acte în original. Fotocopiile sunt suficiente pentru instanță. Dacă lipsesc acte, le putem obține împreună prin demersuri oficiale. Primești răspunsuri clare la toate întrebările de mai sus înainte de a semna orice.',
        },
        {
          title: 'Pasul 2 — Stabilirea strategiei',
          text: 'Analizăm împreună ce bunuri intră în partaj, care este cota ta reală de contribuție și ce probe avem ca să o demonstrăm în fața judecătorului. Îți spun la ce te poți aștepta în mod realist de la instanță, nu ce ai vrea să auzi.',
        },
        {
          title: 'Pasul 3 — Redactarea și depunerea dosarului',
          text: 'Redactez cererea de partaj cu toate bunurile și cererile accesorii. Îți trimit documentul înainte de depunere ca să fii confortabil cu ce se cere. Dosarul este depus la instanță și comunicat celeilalte părți.',
        },
        {
          title: 'Pasul 4 — Expertiza bunurilor',
          text: 'În cadrul dosarului de partaj, judecătorul numește un expert, adică un specialist independent, care evaluează bunurile: casa, mașina, terenul, mobila. Îți explic exact ce înseamnă această evaluare, cum te pregătești pentru ea și cum ne folosim de rezultat în dosar.',
        },
        {
          title: 'Pasul 5 — Reprezentarea în instanță',
          text: 'Mă prezint la fiecare termen de judecată. Ești informat după fiecare dată despre stadiul dosarului, ce s-a decis și ce urmează. Nimic nu se întâmplă fără să știi.',
        },
      ],
    },
    forWhom: {
      forTitle: 'Pachetul de Partaj este pentru tine dacă',
      forItems: [
        'Ești divorțat sau în proces de divorț și ai bunuri comune neîmpărțite cu fostul soț, iar situația aceasta te blochează să îți continui viața.',
        'Vrei să îți recuperezi dreptul de proprietate și să poți decide singur ce faci cu bunurile tale, fără să depinzi de decizia fostului soț.',
        'Vrei să știi de la început care sunt costurile reale, fără surprize pe parcurs, și ce poți obține concret în funcție de situația ta.',
      ],
      notForTitle: 'Pachetul de Partaj nu este pentru tine dacă',
      notForItems: [
        'Ești convins că te vei înțelege cu fostul soț la notar fără nicio dificultate, ai acordul lui și toate actele în regulă, și nu ai nevoie de reprezentare juridică.',
        'Nu ești hotărât dacă vrei să îți valorifici drepturile și amâni decizia de la lună la lună, fără un plan clar de acțiune.',
        'Crezi că dacă mai aștepți, lucrurile se vor rezolva de la sine sau fostul soț îți va oferi mai mult dacă stai, ceea ce în realitate se întâmplă foarte rar.',
      ],
    },
    included: {
      title: 'Hai să vedem ce include Pachetul de Partaj',
      items: [
        'Sesiunea inițială de consultanță și analiza bunurilor care intră în partaj (150 EUR + TVA, aplicați integral la pachet dacă decidem să continuăm)',
        'Clarificări complete despre taxe, probe, procedură și ce poți obține concret înainte de a porni orice dosar',
        'Stabilirea strategiei și a cotei de proprietate pe care o poți cere în instanță',
        'Redactarea cererii de partaj și depunerea dosarului la instanță',
        'Comunicarea cu cealaltă parte și răspuns la toate actele depuse de aceasta',
        'Însoțire și pregătire pentru expertiza bunurilor',
        'Reprezentare la fiecare termen de judecată',
        'Comunicare permanentă pe email și WhatsApp pe tot parcursul dosarului',
      ],
      ctaText: 'Rezervă Sesiunea de Claritate și află prețul pachetului',
      ctaSmall: CTA_SMALL,
    },
    testimonials: {
      title: 'Ce spun clienții care au trecut prin dosarul de partaj cu mine',
    },
    faq: {
      title: 'Întrebări frecvente despre Pachetul de Partaj',
      items: [
        {
          q: 'Pot cere partajul dacă nu am divorțat încă?',
          a: 'Da. Partajul se poate cere în timpul dosarului de divorț sau oricând după ce divorțul s-a finalizat. Recomand să îl introduci odată cu divorțul, pentru că există situații în care este în avantajul tău să meargă împreună. Analizăm ce variantă este mai bună pentru tine în sesiunea de consultanță.',
        },
        {
          q: 'Am nevoie de acordul fostului soț pentru partaj?',
          a: 'Nu. Oricare dintre foștii soți poate introduce cererea de partaj la instanță fără acordul celuilalt. Instanța soluționează independent, indiferent dacă cealaltă parte răspunde sau nu la dosar.',
        },
        {
          q: 'Cât costă taxa de timbru pentru partaj?',
          a: 'Taxa se calculează în funcție de valoarea bunurilor din certificatul fiscal, care este de obicei mai mică decât valoarea de piață, deci este de obicei mai mică decât estimează clienții. Statul a redus această taxă cu 50% recent. Dacă există dovezi de violență domestică, poți fi scutit complet de ea.',
        },
        {
          q: 'Pot obține mai mult de jumătate din bunuri?',
          a: 'Da. Dacă demonstrăm că ai contribuit mai mult la achiziționarea bunurilor din căsătorie, poți obține o cotă mai mare, chiar și de 90% din valoarea bunurilor. Analizăm ce probe sunt disponibile în sesiunea de consultanță și stabilim ce este realizabil.',
        },
        {
          q: 'Se împart și datoriile sau doar bunurile?',
          a: 'Și datoriile și creditele contractate în timpul căsătoriei se împart între foștii soți. Le includem în cererea de partaj și le tratăm cu aceeași atenție ca bunurile.',
        },
        {
          q: 'Este mai avantajos partajul la notar sau la instanță?',
          a: 'Costurile sunt similare în ambele variante. Diferența esențială este că la notar ai nevoie de acordul fostului soț și de toate actele complete. La instanță nu ai nevoie de acordul lui. Dacă fostul soț nu cooperează sau lipsesc acte, instanța este singura cale disponibilă.',
        },
        {
          q: 'Ce se întâmplă dacă fostul soț cedează în timpul procesului?',
          a: 'Poți face o înțelegere, numită tranzacție, în orice moment al procesului și să încheiați dosarul mai repede prin acord. Deschiderea dosarului de partaj nu blochează negocierea dintre voi, dimpotrivă, adesea o grăbește pentru că cealaltă parte realizează că procesul merge înainte oricum.',
        },
      ],
    },
  },

  divortsipartaj: {
    slug: 'divortsipartaj',
    category: 'Divorț & Partaj',
    badge: 'PACHET COMPLET',
    image:
      'https://images.unsplash.com/photo-1450101499163-c8848c66ca85?w=800&h=600&fit=crop',
    shortDescription:
      'Rezolvi tot dintr-o singură mișcare: desfaci căsătoria și îți recuperezi proprietățile.',
    hero: {
      title: 'Pachetul Complet Divorț și Partaj',
      subtitle:
        'Rezolvi tot dintr-o singură mișcare. Desfaci căsătoria și îți recuperezi proprietățile, cu o singură strategie, un singur avocat și transparență completă de la început până la final.',
    },
    awareness: {
      title:
        'Mulți oameni divorțează și lasă partajul pe mai târziu. Asta îi costă scump.',
      paragraphs: [
        'Ai divorțat sau ești pe cale să divorțezi, dar bunurile comune sunt încă neîmpărțite. Casa este pe amândoi. Mașina la fel. Poate un credit. Poate mai multe proprietăți.',
        'Cu cât aștepți, cu atât situația devine mai complicată. Copiii cresc și prezența lor devine un factor în ecuație. Poate apare o recăsătorire, un deces, un alt copil. Fiecare an care trece adaugă un element care face împărțirea bunurilor mai grea.',
        'Există o cale mai inteligentă: să ceri divorțul și partajul în același dosar, de la început. Instanța poate soluționa divorțul mai repede și să lase partajul pe rol, dar tu ai deja totul în mișcare dintr-o singură acțiune.',
      ],
    },
    benefits: {
      title: 'Ce obții cu Pachetul Complet Divorț și Partaj',
      items: [
        'O singură sesiune de consultanță în care analizăm tot: desfacerea căsătoriei, copiii, bunurile, datoriile și ce strategie este cea mai bună pentru tine.',
        'Divorțul și partajul merg în paralel. Divorțul se poate soluționa mai repede, iar partajul continuă pe rol, fără să fii nevoit să începi un dosar separat mai târziu.',
        'Economisești timp și bani față de a face două dosare separate, cu două seturi de proceduri și două perioade de pregătire.',
        'Dacă există bunuri de evaluat prin expertiză, adică un specialist venit de la instanță care evaluează casa, mașina sau terenul, totul se face în același dosar, sub aceeași coordonare.',
        'Strategia pentru copii și strategia pentru bunuri se construiesc împreună, nu separat, ceea ce dă o imagine completă și coerentă în fața judecătorului.',
        'Ai un singur interlocutor, eu, pentru tot dosarul. Nu explici situația de două ori, nu gestionezi doi avocați, două dosare, două calendare de termene.',
      ],
    },
    howWeHelp: {
      title: 'Ce clarificăm împreună înainte de a porni dosarul',
      intro:
        'Nu toate căsătoriile supraviețuiesc. Când ajungi la concluzia că separarea este soluția, cel mai important lucru este să știi că nu ești singur în acest proces. Personalizăm sfaturile pentru situația ta concretă și rămânem alături de tine prin tot ceea ce este adesea o experiență dificilă și emoțională.',
      items: [
        'Procedura de divorț prin instanță, pas cu pas, pe înțelesul tău',
        'Ce probe trebuie strânse și cum le administrăm în timpul procesului',
        'Taxa de timbru pentru divorț și cât costă în realitate',
        'La ce judecătorie se introduce cererea și de ce contează asta',
        'Ce soluții poate pronunța instanța și la ce să te aștepți',
        'Cât durează un proces de divorț în funcție de situația ta',
        'Cine poate veni ca martor și cum pregătim martorii',
        'Ce se întâmplă dacă celălalt soț nu este de acord cu divorțul',
        'Dacă poți divorța fără să faci partaj în același timp',
        'Ce acte sunt necesare pentru depunerea dosarului',
      ],
    },
    process: {
      title: 'Cum lucrăm împreună de la primul pas până la final',
      steps: [
        {
          title: 'Pasul 1 — Sesiunea de Claritate',
          text: 'Îmi prezinți situația completă: relația, copiii, bunurile, datoriile, ce îți dorești să obții. Stabilim împreună dacă este mai avantajos să introducem partajul odată cu divorțul sau separat. Primești răspunsuri clare la toate întrebările de mai sus înainte de a semna orice.',
        },
        {
          title: 'Pasul 2 — Redactarea dosarului complet',
          text: 'Redactez o singură acțiune care cuprinde toate cererile: divorțul, custodia, pensia de întreținere, programul de vizită, atribuirea locuinței și partajul bunurilor comune. Îți trimit documentul înainte de depunere ca să fii confortabil cu fiecare cuvânt scris acolo.',
        },
        {
          title: 'Pasul 3 — Procedura prealabilă',
          text: 'Cealaltă parte primește dosarul și are 25 de zile să răspundă cu propriile cereri. Răspundem împreună la tot ce depune.',
        },
        {
          title: 'Pasul 4 — Soluționarea divorțului',
          text: 'Instanța poate soluționa divorțul mai repede și lăsa partajul pe rol. Rămânem împreună pe tot parcursul ambelor proceduri, indiferent cât durează.',
        },
        {
          title: 'Pasul 5 — Expertiza și soluționarea partajului',
          text: 'Un expert numit de judecător evaluează bunurile comune. Noi administrăm toate probele și te reprezint la fiecare termen până la soluționarea finală.',
        },
      ],
    },
    forWhom: {
      forTitle: 'Pachetul Complet este pentru tine dacă',
      forItems: [
        'Vrei să rezolvi totul odată: divorțul, copiii și bunurile, fără să lași lucruri neîncheiate care să te urmărească ani de zile.',
        'Ai bunuri comune semnificative, cum ar fi o casă, mașini, terenuri sau investiții, și vrei să îți recuperezi drepturile din prima, nu după ani de așteptare.',
        'Vrei un singur avocat care să cunoască dosarul complet și să aibă o imagine de ansamblu, nu să explici situația de mai multe ori unor oameni diferiți.',
      ],
      notForTitle: 'Pachetul Complet nu este pentru tine dacă',
      notForItems: [
        'Nu ai bunuri comune de împărțit și vrei doar să desfaci căsătoria, caz în care Pachetul de Divorț este suficient.',
        'Ești deja în mijlocul unui divorț finalizat și ai nevoie doar de partaj separat, caz în care există Pachetul de Partaj dedicat acestei situații.',
        'Nu ești hotărât că vrei să pornești ambele procese acum și preferi să iei o decizie mai târziu după ce te mai gândești.',
      ],
    },
    included: {
      title: 'Hai să vedem ce include Pachetul Complet Divorț și Partaj',
      items: [
        'Sesiunea inițială de consultanță și analiza completă a situației tale (150 EUR + TVA, aplicați integral la pachet dacă decidem să continuăm)',
        'Clarificări complete despre procedură, taxe, probe, termene și judecătorie înainte de a începe',
        'Redactarea dosarului complet cu toate cererile: divorț, custodie, pensie, program de vizită și partaj',
        'Depunerea dosarului și comunicarea cu instanța',
        'Procedura prealabilă și răspuns la actele depuse de cealaltă parte',
        'Însoțire la expertiza bunurilor și pregătire pentru ce urmează',
        'Reprezentare la fiecare termen de judecată, pe tot parcursul ambelor dosare',
        'Comunicare permanentă pe email și WhatsApp',
      ],
      ctaText: 'Rezervă Sesiunea de Claritate și află prețul pachetului',
      ctaSmall: CTA_SMALL,
    },
    testimonials: {
      title: 'Ce spun clienții care au ales Pachetul Complet',
    },
    faq: {
      title: 'Întrebări frecvente despre Pachetul Complet Divorț și Partaj',
      items: [
        {
          q: 'Este obligatoriu să fac partajul odată cu divorțul?',
          a: 'Nu este obligatoriu, dar este recomandat în multe situații. Există cazuri în care este mai avantajos strategic să introduci partajul odată cu divorțul. Analizăm situația ta în sesiunea de consultanță și decidem împreună ce variantă îți este mai favorabilă.',
        },
        {
          q: 'Ce se întâmplă dacă divorțul se soluționează mai repede decât partajul?',
          a: 'Este posibil și frecvent. Divorțul poate fi pronunțat mai rapid, iar partajul rămâne pe rol și continuă. Nu pierzi nimic, ci ai deja procesul în mișcare și nu mai trebuie să pornești un dosar nou de la zero.',
        },
        {
          q: 'Ce înseamnă expertiza bunurilor și cât durează?',
          a: 'Expertiza înseamnă că un specialist numit de judecător vine să evalueze bunurile comune: casa, mașina, terenul, mobila. Evaluarea aceasta face parte din procedura de partaj și adaugă un termen suplimentar. Îți explic exact ce implică și cum te pregătești pentru ea.',
        },
        {
          q: 'Pot face partajul la notar după ce divorțez, fără instanță?',
          a: 'Da, dacă fostul soț este de acord și aveți toate actele în regulă. Dacă nu există acord sau lipsesc acte, instanța este singura cale. Costurile sunt similare în ambele variante, dar la notar depinzi de bunăvoința celeilalte persoane.',
        },
        {
          q: 'Ce bunuri se împart în partaj?',
          a: 'Toate bunurile cumpărate în timpul căsătoriei cu bani comuni: case, mașini, economii, investiții, mobilă, terenuri. Nu se împart bunurile avute înainte de căsătorie și nici cele primite prin moștenire sau donație de la un singur soț.',
        },
      ],
    },
  },

  mosteniri: {
    slug: 'mosteniri',
    category: 'Moșteniri',
    badge: 'MOȘTENIRI',
    image:
      'https://images.unsplash.com/photo-1589829545856-d10d557cf95f?w=800&h=600&fit=crop',
    shortDescription:
      'Dezbați moștenirea și îți obții drepturile chiar dacă nu ai toate actele sau un moștenitor refuză.',
    hero: {
      title: 'Pachetul de Moșteniri',
      subtitle:
        'Dezbați moștenirea și îți obții drepturile chiar dacă nu ai toate actele, chiar dacă un moștenitor refuză să semneze și chiar dacă nu ești în țară.',
    },
    awareness: {
      title:
        'Ai o moștenire nedezbătută și nu poți face nimic cu ea. De ani de zile.',
      paragraphs: [
        'Ai pierdut un părinte, un bunic sau o rudă și moștenirea nu a fost dezbătută niciodată. Poate lipsesc acte. Poate un moștenitor refuză să vină la notar sau să coopereze. Poate sunt moștenitori care locuiesc în alte țări. Poate moștenirea s-a lăsat nerezolvată de două sau trei generații.',
        'Rezultatul concret este că tu ești proprietar pe hârtie, dar nu poți face nimic cu acea proprietate. Nu o poți vinde, nu o poți închiria, nu o poți transmite mai departe.',
        'Prin instanță, moștenirea se poate dezbate indiferent dacă un moștenitor refuză, indiferent dacă actele sunt incomplete și indiferent dacă nu ești în țară. Nu ai nevoie de acordul nimănui ca să pornești procesul.',
      ],
    },
    benefits: {
      title: 'Ce obții lucrând cu mine pe dosarul de moștenire',
      items: [
        'Poți dezbate moștenirea chiar dacă nu ai toate actele. La instanță se depun fotocopii, nu originale, și pot fi obținute actele lipsă prin demersuri oficiale.',
        'Nu ai nevoie de acordul celorlalți moștenitori. Oricare dintre ei poate introduce cererea la instanță și procesul merge înainte, indiferent de atitudinea celorlalți.',
        'Dacă ești în altă țară, lucrăm la distanță. Contractul se semnează online, actele se trimit digital și eu te reprezint la toate termenele de judecată.',
        'Dacă un moștenitor nu poate fi găsit sau nu se prezintă, există soluții legale clare. Se poate numi un avocat din oficiu pentru el sau se fac demersuri la Evidența Populației pentru a-i afla domiciliul.',
        'Obții certitudinea juridică că drepturile tale sunt clare, că nimeni nu îți poate contesta calitatea de moștenitor și că poți dispune liber de cota ta din moștenire.',
        'Pot fi dezbătute moșteniri lăsate deschise de mai multe generații, chiar și fără acte complete pe proprietăți construite fără autorizație sau neintabulate, adică neînregistrate oficial.',
      ],
    },
    howWeHelp: {
      title: 'Ce clarificăm împreună înainte de a porni dosarul',
      intro:
        'Moștenirile nedezbătute sunt printre cele mai frecvente situații cu care vin clienții la mine. De mulți ani, uneori de generații. Motivele sunt mereu aceleași: lipsesc acte, cineva nu vrea să coopereze, nimeni nu știe exact de unde să înceapă. Înainte de orice dosar, îți explicăm exact ce este posibil pentru situația ta concretă și care este primul pas.',
      items: [
        'Dacă poți dezbate moștenirea fără acordul tuturor moștenitorilor și cum funcționează asta',
        'Ce se întâmplă dacă un moștenitor refuză să vină la instanță sau nu poate fi găsit',
        'Ce acte sunt necesare și ce faci dacă nu le ai pe toate sau lipsesc complet',
        'Dacă poți dezbate moștenirea de la distanță, din altă țară, fără să vii în România',
        'Ce se întâmplă cu proprietățile care nu au acte sau nu sunt intabulate',
        'Cum se calculează taxa de timbru pentru moștenire și cât costă în realitate',
        'Cine are drept de moștenire și în ce ordine și cât primește fiecare',
        'Ce se întâmplă dacă un moștenitor a luat deja bunuri sau bani înainte de dezbaterea oficială',
        'Dacă există un testament și ce efect are asupra drepturilor tale',
        'Cât durează un dosar de moștenire și ce termene sunt de așteptat',
      ],
    },
    process: {
      title: 'Cum lucrăm împreună de la primul pas până la final',
      steps: [
        {
          title: 'Pasul 1 — Sesiunea de Claritate',
          text: 'Îmi prezinți situația: cine a decedat, ce bunuri există, câți moștenitori sunt și ce acte ai disponibile. Analizăm ce este posibil, care este varianta ideală pentru tine și ce urmează concret. Primești răspunsuri clare la toate întrebările de mai sus înainte de a semna orice.',
        },
        {
          title: 'Pasul 2 — Obținerea actelor lipsă',
          text: 'Dacă lipsesc certificate de deces, certificate de moștenitor, acte de proprietate sau alte documente, facem demersurile necesare pentru a le obține prin canale oficiale. Absența actelor nu oprește procesul.',
        },
        {
          title: 'Pasul 3 — Redactarea dosarului și depunerea la instanță',
          text: 'Redactez cererea de dezbatere a moștenirii cu toate bunurile și toți moștenitorii incluși. Îți trimit documentul înainte de depunere ca să fii confortabil cu ce se cere. Dosarul este depus la instanță.',
        },
        {
          title: 'Pasul 4 — Procedura în instanță',
          text: 'Ceilalți moștenitori sunt citați oficial de instanță. Dacă un moștenitor nu se prezintă sau nu răspunde, instanța poate numi un avocat din oficiu pentru el, adică un avocat plătit de stat care îl reprezintă în dosar. Administrăm toate probele necesare.',
        },
        {
          title: 'Pasul 5 — Soluționarea dosarului',
          text: 'La final, fiecare moștenitor primește certificatul care îi dovedește dreptul de proprietate și poate decide liber ce face cu cota sa, adică o vinde, o păstrează sau o transmite mai departe.',
        },
      ],
    },
    forWhom: {
      forTitle: 'Pachetul de Moșteniri este pentru tine dacă',
      forItems: [
        'Ai o moștenire nedezbătută de ani de zile, poate chiar de generații, și vrei să îți clarifici drepturile de proprietate o dată pentru totdeauna.',
        'Un moștenitor refuză să coopereze la notar sau nu poate fi găsit și vrei să rezolvi situația pe cale legală, fără să depinzi de bunăvoința lui.',
        'Nu ai toate actele sau proprietățile nu sunt complet intabulate, ceea ce face imposibilă dezbaterea la notar, dar nu și la instanță.',
      ],
      notForTitle: 'Pachetul de Moșteniri nu este pentru tine dacă',
      notForItems: [
        'Toți moștenitorii sunt de acord, ai toate actele complete și preferi să mergi la notar, ceea ce este o cale perfect valabilă atunci când condițiile sunt îndeplinite.',
        'Nu ești hotărât dacă vrei să îți exerciți dreptul de moștenire și preferi să mai aștepți înainte de a lua o decizie.',
        'Cauți garanții că procesul va dura o perioadă exactă de timp, deoarece durata depinde de instanță și de complexitatea situației și nu poate fi garantată în avans.',
      ],
    },
    included: {
      title: 'Hai să vedem ce include Pachetul de Moșteniri',
      items: [
        'Sesiunea inițială de consultanță și analiza situației succesorale (150 EUR + TVA, aplicați integral la pachet dacă decidem să continuăm)',
        'Clarificări complete despre drepturi, acte necesare, taxe și procedură înainte de a porni orice dosar',
        'Demersuri pentru obținerea actelor lipsă: certificate de deces, acte de proprietate, certificate de moștenitor',
        'Redactarea cererii de dezbatere a moștenirii și depunerea la instanță',
        'Citarea tuturor moștenitorilor și gestionarea situațiilor în care aceștia nu cooperează sau nu pot fi găsiți',
        'Administrarea tuturor probelor necesare în dosar',
        'Reprezentare la fiecare termen de judecată',
        'Comunicare permanentă pe email și WhatsApp, inclusiv cu clienți din afara României',
      ],
      ctaText: 'Rezervă Sesiunea de Claritate și află prețul pachetului',
      ctaSmall: CTA_SMALL,
    },
    testimonials: {
      title: 'Ce spun clienții care și-au rezolvat moștenirile cu mine',
    },
    faq: {
      title: 'Întrebări frecvente despre Pachetul de Moșteniri',
      items: [
        {
          q: 'Am nevoie de acordul celorlalți moștenitori pentru a deschide dosarul?',
          a: 'Nu. Oricare moștenitor poate introduce singur cererea de dezbatere a moștenirii la instanță, fără acordul celorlalți. Instanța soluționează independent, indiferent dacă ceilalți moștenitori răspund sau nu.',
        },
        {
          q: 'Ce fac dacă nu știu unde locuiește un moștenitor?',
          a: 'Se fac demersuri la Direcția de Evidență a Populației pentru a afla domiciliul acestuia. Dacă moștenitorul nu poate fi găsit în niciun fel, instanța numește un avocat din oficiu, adică un avocat plătit de stat, care îl reprezintă în dosar. Procesul merge înainte în orice situație.',
        },
        {
          q: 'Pot dezbate moștenirea dacă nu am actele pe casă?',
          a: 'Da. La instanță se depun fotocopii, nu originale. Dacă actele lipsesc complet, putem face demersuri pentru a le obține sau reconstitui prin mijloace legale. Fiecare situație se analizează individual în sesiunea de consultanță.',
        },
        {
          q: 'Cât costă taxa de timbru pentru moștenire?',
          a: 'Taxa se calculează în funcție de valoarea bunurilor din certificatul fiscal, care este de obicei mai mică decât valoarea de piață. La finalul procesului, taxa se împarte proporțional cu dreptul fiecărui moștenitor. Îți explic exact suma estimativă în sesiunea de consultanță, înainte de a decide orice.',
        },
        {
          q: 'Cât durează un dosar de moștenire?',
          a: 'Poate dura șase luni sau mai mult, în funcție de complexitatea situației și de instanța la care se judecă dosarul. Important este că odată deschis dosarul, forțezi situația să se miște. Ceilalți moștenitori trebuie să răspundă în fața judecătorului, ceea ce duce de obicei la soluții mai rapide decât așteptarea pasivă.',
        },
      ],
    },
  },

  pachetexecutari: {
    slug: 'pachetexecutari',
    category: 'Executări',
    badge: 'EXECUTĂRI SILITE',
    image:
      'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=800&h=600&fit=crop',
    shortDescription:
      'Ai o hotărâre judecătorească nerespectată. Obligi cealaltă parte să respecte ce a decis instanța.',
    hero: {
      title: 'Pachetul de Executări Silite',
      subtitle:
        'Ai o hotărâre judecătorească sau o obligație legală nerespectată. Există mijloace legale prin care poți obliga cealaltă parte să respecte ce a decis instanța.',
    },
    awareness: {
      title:
        'Ai câștigat în instanță, dar cealaltă parte nu respectă hotărârea. Ce faci acum?',
      paragraphs: [
        'Ai obținut o hotărâre judecătorească: pensie de întreținere, partaj, daune, obligații de plată. Instanța a decis în favoarea ta. Dar cealaltă parte nu plătește, nu predă bunurile sau ignoră pur și simplu ceea ce a stabilit judecătorul.',
        'Poate fostul soț nu achită pensia de întreținere de luni de zile. Poate refuză să elibereze locuința sau să predea un bun care ți-a fost atribuit prin hotărâre.',
        'Executarea silită este mecanismul legal prin care obligi cealaltă parte să respecte hotărârea, prin sechestru pe conturi, poprire pe salariu sau alte măsuri de constrângere legală. Adică prin forța legii, nu prin rugăminți.',
      ],
    },
    benefits: {
      title: 'Ce obții lucrând cu mine pe dosarul de executare silită',
      items: [
        'Pornești executarea silită cu actele corecte și prin executorul judecătoresc potrivit pentru situația ta, nu orice executor ales la întâmplare.',
        'Poprire pe salariu sau pe conturi bancare, dacă debitorul, adică persoana care trebuie să plătească, are venituri sau economii. Banii pot fi recuperați direct de la angajator sau de la bancă.',
        'Sechestru pe bunuri mobile sau imobile, adică pe proprietăți, mașini sau alte bunuri, dacă debitorul deține ceva care poate acoperi datoria.',
        'Dacă fostul soț nu achită pensia de întreținere, executarea silită poate fi combinată cu o plângere penală, ceea ce crește presiunea legală semnificativ.',
        'Te reprezint în relația cu executorul judecătoresc și mă asigur că procedura este aplicată corect și exclusiv în interesul tău.',
      ],
    },
    howWeHelp: {
      title: 'Ce clarificăm împreună înainte de a porni dosarul',
      intro:
        'Multe persoane care au câștigat în instanță nu știu că hotărârea nu se aplică automat. Trebuie să faci un pas suplimentar, adică să pornești executarea silită, pentru ca decizia judecătorului să producă efecte concrete. Înainte de orice dosar de executare, îți explicăm exact ce ai la dispoziție și care este cea mai eficientă cale pentru situația ta.',
      items: [
        'Ce este executarea silită și de ce hotărârea instanței nu se aplică automat fără acest pas',
        'Ce forme de executare există și care este cea mai potrivită pentru situația ta',
        'Dacă poți porni executarea dacă nu știi unde lucrează debitorul sau ce conturi are',
        'Cum funcționează poprirea pe salariu și cât se poate reține lunar din venitul debitorului',
        'Cum funcționează sechestrul pe bunuri și ce bunuri pot fi urmărite',
        'Ce se întâmplă dacă debitorul nu are venituri sau bunuri urmăribile în acest moment',
        'Dacă executarea silită și plângerea penală pot merge în același timp',
        'Cât durează recuperarea banilor prin executare silită și ce termene sunt de așteptat',
        'Ce costuri are executarea și cine le suportă în final',
        'Ce se întâmplă dacă debitorul încearcă să ascundă bunurile sau să se sustragă executării',
      ],
    },
    process: {
      title: 'Cum lucrăm împreună de la primul pas până la final',
      steps: [
        {
          title: 'Pasul 1 — Sesiunea de Claritate',
          text: 'Îmi prezinți situația: ce hotărâre ai, ce nu se respectă, cât timp a trecut și ce știi despre situația financiară a debitorului. Primești răspunsuri la toate întrebările de mai sus și stabilim împreună care este cea mai eficientă cale de acțiune.',
        },
        {
          title: 'Pasul 2 — Alegerea formei de executare',
          text: 'Stabilim împreună cea mai eficientă formă de executare pentru cazul tău concret: poprire pe salariu, poprire pe conturi bancare, sechestru pe bunuri sau o combinație între acestea. Nu există o soluție universală, fiecare situație se analizează separat.',
        },
        {
          title: 'Pasul 3 — Dosarul de executare',
          text: 'Pregătesc toate actele necesare și însoțesc dosarul la biroul executorului judecătoresc. Mă asigur că totul este corect și complet pentru ca executarea să poată fi demarată fără întârzieri cauzate de lipsuri de formă.',
        },
        {
          title: 'Pasul 4 — Urmărirea executării',
          text: 'Urmăresc derularea executării și îți comunic la fiecare pas important ce se întâmplă, ce s-a recuperat și ce urmează. Nu afli din întâmplare cum merge situația.',
        },
      ],
    },
    forWhom: {
      forTitle: 'Pachetul de Executări Silite este pentru tine dacă',
      forItems: [
        'Ai o hotărâre judecătorească definitivă, adică o hotărâre care nu mai poate fi atacată sau care a fost deja atacată și menținută, și cealaltă parte nu o respectă.',
        'Pensia de întreținere nu a fost achitată de luni de zile și vrei să recuperezi sumele restante prin forța legii, nu prin negocieri repetate.',
        'Un bun care ți-a fost atribuit prin hotărâre nu a fost predat sau o sumă de bani nu a fost plătită și vrei să acționezi concret.',
      ],
      notForTitle: 'Pachetul de Executări Silite nu este pentru tine dacă',
      notForItems: [
        'Nu ai încă o hotărâre judecătorească definitivă sau un titlu executoriu, adică un document legal care confirmă obligația celeilalte părți. Fără acesta, executarea silită nu poate fi pornită.',
        'Cauți o soluție prin care să obții banii sau bunurile în câteva zile. Executarea are propriile termene legale și depinde de situația financiară a debitorului.',
        'Vrei să forțezi respectarea unui acord verbal sau a unei înțelegeri neoficiale care nu a fost confirmată printr-o hotărâre judecătorească.',
      ],
    },
    included: {
      title: 'Hai să vedem ce include Pachetul de Executări Silite',
      items: [
        'Sesiunea inițială de consultanță și analiza titlului executoriu (150 EUR + TVA, aplicați integral la pachet dacă decidem să continuăm)',
        'Clarificări complete despre formele de executare disponibile și care este cea mai potrivită pentru situația ta',
        'Alegerea formei optime de executare în funcție de situația financiară a debitorului',
        'Redactarea cererii de executare și depunerea la biroul executorului judecătoresc',
        'Comunicarea cu executorul și urmărirea procedurii',
        'Informare permanentă despre stadiul executării la fiecare pas important',
      ],
      ctaText: 'Rezervă Sesiunea de Claritate și află prețul pachetului',
      ctaSmall: CTA_SMALL,
    },
    testimonials: {
      title: 'Ce spun clienții care au folosit executarea silită',
    },
    faq: {
      title: 'Întrebări frecvente despre executările silite',
      items: [
        {
          q: 'Pot porni executarea dacă nu știu unde lucrează debitorul sau ce conturi are?',
          a: 'Da. Executorul judecătoresc are atribuții legale de cercetare și poate face interogări la ANAF, la bănci și la angajatori pentru a identifica veniturile și bunurile debitorului. Nu depinzi de informațiile pe care le ai tu în acest moment.',
        },
        {
          q: 'Cât durează recuperarea banilor prin executare silită?',
          a: 'Depinde de situația financiară a debitorului și de forma de executare aleasă. Popririle pe salariu, adică reținerea directă din salariu, sunt de obicei cele mai rapide. Sechestrele pe bunuri pot dura mai mult, deoarece implică evaluarea și vânzarea bunurilor. Analizăm situația ta concretă și îți spun la ce să te aștepți.',
        },
        {
          q: 'Ce se întâmplă dacă debitorul nu are bunuri sau venituri?',
          a: 'Executarea se suspendă dacă debitorul nu are nimic urmăribil în acel moment. Există totuși posibilitatea de a relua executarea ulterior, dacă situația financiară a debitorului se schimbă. Discutăm opțiunile disponibile în sesiunea de consultanță.',
        },
        {
          q: 'Pot combina executarea silită cu o plângere penală?',
          a: 'Da, în cazul neplății pensiei de întreținere, cele două proceduri pot merge în paralel. Executarea silită urmărește recuperarea banilor. Plângerea penală adaugă o presiune suplimentară prin consecințele penale pe care le riscă debitorul. Combinarea lor este adesea cea mai eficientă strategie.',
        },
        {
          q: 'Cine plătește costurile executării silite?',
          a: 'Costurile executării silite, adică onorariul executorului judecătoresc și cheltuielile procedurale, sunt în principiu suportate de debitor, adică de persoana care nu a respectat hotărârea. Îți explic exact cum funcționează asta în sesiunea de consultanță, în funcție de situația ta concretă.',
        },
      ],
    },
  },

  pensiesiprogram: {
    slug: 'pensiesiprogram',
    category: 'Penal',
    badge: 'PACHET PENAL',
    image:
      'https://images.unsplash.com/photo-1589994965851-a8f479c573a9?w=800&h=600&fit=crop',
    shortDescription:
      'Pensia neplătită și programul de vizită ignorat sunt infracțiuni. Cel vinovat răspunde penal.',
    hero: {
      title:
        'Pachetul Penal pentru Pensie Neachitată și Program de Vizită Nerespectat',
      subtitle:
        'Există lege care obligă părinții să plătească pensia de întreținere și să respecte programul de vizită stabilit de instanță. Dacă nu o fac, răspund penal.',
    },
    awareness: {
      title:
        'Pensia de întreținere nu vine de luni de zile. Programul de vizită este ignorat. Și tu nu știi ce poți face.',
      paragraphs: [
        'Ai o hotărâre judecătorească clară. Pensia de întreținere trebuie plătită lunar. Programul de vizită este stabilit. Dar fostul soț nu plătește. Sau nu respectă programul și copilul nu mai ajunge la tine.',
        'Poate ți s-a spus că nu poți face nimic dacă nu are bani. Sau că instanța nu te ajută mai mult. Sau că procesul penal este complicat și îndelungat.',
        'Legea română pedepsește neplata pensiei de întreținere și nerespectarea programului de vizită ca infracțiuni penale. Adică fapte pentru care cel vinovat poate fi tras la răspundere în fața unui judecător. Există pași clari pe care îi putem face pentru a-l obliga pe celălalt să respecte ceea ce a decis instanța.',
      ],
    },
    benefits: {
      title: 'Ce obții lucrând cu mine pe dosarul penal',
      items: [
        'Depunem plângerea penală la parchet sau la poliție cu toate actele necesare, corect redactate, astfel încât dosarul să fie primit și procesat fără greșeli de formă.',
        'Presiunea unui dosar penal schimbă frecvent comportamentul celui care nu plătește sau nu respectă programul. Mulți ajung să regleze situația din proprie inițiativă după ce primesc citația.',
        'Dacă există condamnare penală, aceasta poate duce la obligarea debitorului la plata sumelor restante și poate fi folosită în alte proceduri civile conexe.',
        'Îți explic exact ce probe trebuie să aduni: extrase de cont, mesaje, email-uri, martori, și cum să le prezinți corect pentru a fi luate în considerare.',
        'Mă ocup de toată comunicarea cu organele de cercetare penală, adică poliția și parchetul, și îți traduc fiecare pas în termeni simpli, fără limbaj juridic complicat.',
      ],
    },
    howWeHelp: {
      title: 'Ce clarificăm împreună înainte de a porni dosarul',
      intro:
        'Mulți oameni nu știu că neplata pensiei de întreținere și nerespectarea programului de vizită sunt infracțiuni penale, nu doar probleme civile. Asta înseamnă că există un instrument legal cu o forță cu totul diferită față de o simplă executare silită. Înainte de orice dosar, îți explicăm exact ce ai la dispoziție și cum funcționează în realitate.',
      items: [
        'De câte luni de neplată ai nevoie ca să poți depune plângere penală și ce spune legea exact',
        'Ce se întâmplă după ce depui plângerea și care sunt etapele cercetării penale',
        'Dacă poți cere și banii restanți prin dosarul penal sau printr-o cale separată',
        'Ce probe sunt necesare pentru neplata pensiei și cum le documentezi corect',
        'Ce probe sunt necesare pentru nerespectarea programului de vizită și cum le strângi',
        'Dacă plângerea penală și executarea silită pot merge în același timp',
        'Ce se întâmplă dacă cel împotriva căruia depui plângerea susține că nu a avut bani',
        'Cât durează cercetarea penală și la ce termene să te aștepți',
        'Ce efect are dosarul penal asupra relației cu cealaltă parte și asupra copilului',
        'Cum protejăm copilul în tot acest proces și ce îi spunem sau nu îi spunem',
      ],
    },
    process: {
      title: 'Cum lucrăm împreună de la primul pas până la final',
      steps: [
        {
          title: 'Pasul 1 — Sesiunea de Claritate',
          text: 'Îmi prezinți situația: de câte luni nu se plătește pensia, ce dovezi ai, cum documentezi nerespectarea programului de vizită. Stabilim ce plângere formulăm, la cine o depunem și ce strategii avem la dispoziție. Primești răspunsuri la toate întrebările de mai sus înainte de orice decizie.',
        },
        {
          title: 'Pasul 2 — Adunarea probelor',
          text: 'Identificăm împreună probele disponibile: extrase de cont care arată că pensia nu a intrat, mesaje în care fostul soț recunoaște că nu plătește, email-uri, martori, comunicări care dovedesc refuzul de a respecta programul de vizită. Îți spun exact ce să păstrezi și ce nu mai poți recupera dacă aștepți.',
        },
        {
          title: 'Pasul 3 — Redactarea și depunerea plângerii penale',
          text: 'Redactez plângerea penală cu toate elementele legale necesare, astfel încât să fie completă și să nu fie returnată pentru lipsuri de formă. O depun la parchetul sau poliția competentă pentru situația ta.',
        },
        {
          title: 'Pasul 4 — Urmărirea dosarului penal',
          text: 'Urmăresc stadiul dosarului la parchet și îți comunic fiecare decizie luată de organele de cercetare penală. Nu afli din întâmplare ce se întâmplă. Ești informat la fiecare pas important.',
        },
      ],
    },
    forWhom: {
      forTitle: 'Pachetul Penal este pentru tine dacă',
      forItems: [
        'Pensia de întreținere nu a fost achitată de cel puțin două luni consecutive și ai dovezi clare că sumele nu au fost virate, nu doar o bănuială.',
        'Celălalt părinte refuză în mod repetat să respecte programul de vizită stabilit prin hotărâre judecătorească și ai dovezi că ai cerut vizita și ți-a fost refuzată.',
        'Vrei o formă de presiune legală mai puternică decât executarea silită, care să determine cealaltă parte să respecte obligațiile stabilite de instanță.',
      ],
      notForTitle: 'Pachetul Penal nu este pentru tine dacă',
      notForItems: [
        'Nu există încă o hotărâre judecătorească care stabilește pensia de întreținere sau programul de vizită. Fără o decizie a instanței, nu există infracțiune și nu se poate depune plângere penală.',
        'Vrei să folosești dosarul penal ca instrument de presiune în alte conflicte care nu au legătură cu pensia sau vizita copilului, ceea ce nu este posibil din punct de vedere legal.',
        'Situația este neclară și nu ai dovezi concrete că plata sau vizita a fost refuzată în mod deliberat și repetat.',
      ],
    },
    included: {
      title:
        'Hai să vedem ce include Pachetul Penal pentru Pensie și Program de Vizită',
      items: [
        'Sesiunea inițială de consultanță și analiza probelor disponibile (150 EUR + TVA, aplicați integral la pachet dacă decidem să continuăm)',
        'Clarificări complete despre condițiile legale, probe necesare și ce se întâmplă după depunerea plângerii',
        'Ghidaj pentru adunarea și documentarea corectă a probelor',
        'Redactarea plângerii penale complete',
        'Depunerea la parchet sau poliție',
        'Urmărirea dosarului penal și comunicare permanentă despre stadiul acestuia',
      ],
      ctaText: 'Rezervă Sesiunea de Claritate și află prețul pachetului',
      ctaSmall: CTA_SMALL,
    },
    testimonials: {
      title: 'Ce spun clienții care au apelat la această soluție legală',
    },
    faq: {
      title:
        'Întrebări frecvente despre dosarul penal pentru pensie și program de vizită',
      items: [
        {
          q: 'De câte luni de neplată am nevoie ca să pot depune plângere penală?',
          a: 'Legea prevede că neplata pensiei de întreținere pentru cel puțin două luni consecutive constituie infracțiune penală. Analizăm situația ta concretă în sesiunea de consultanță și stabilim dacă sunt îndeplinite condițiile pentru a depune plângerea.',
        },
        {
          q: 'Ce se întâmplă după ce depun plângerea?',
          a: 'Parchetul sau poliția demarează cercetarea penală. Cel împotriva căruia ai depus plângerea este citat, audiat și obligat să se prezinte și să dea explicații. Presiunea acestei proceduri determină de multe ori plata voluntară a restanțelor chiar înainte de o eventuală condamnare.',
        },
        {
          q: 'Pot cere și banii restanți pe cale penală?',
          a: 'Da. Dosarul penal poate fi combinat cu o cerere civilă de despăgubire pentru sumele neachitate. Discutăm în sesiunea de consultanță care este cea mai eficientă strategie pentru situația ta, astfel încât să recuperezi atât dreptul copilului la pensie, cât și sumele restante.',
        },
        {
          q: 'Ce probe am nevoie pentru nerespectarea programului de vizită?',
          a: 'Mesaje, email-uri, refuzuri documentate în scris, martori care au asistat la refuz. Important este să ai dovezi că ai solicitat vizita conform programului stabilit prin hotărâre și că ți-a fost refuzată în mod deliberat. Te ghidez exact ce să păstrezi de acum înainte și cum să documentezi fiecare incident.',
        },
        {
          q: 'Fostul soț susține că nu a avut bani. Pot face ceva?',
          a: 'Da. Legea nu acceptă lipsa banilor ca scuză automată pentru neplata pensiei de întreținere. Există obligația legală de a plăti și de a face eforturi pentru a găsi resurse. Analizăm situația ta concretă și stabilim ce argumente și probe sunt relevante pentru cazul tău.',
        },
      ],
    },
  },

  ordindeprotectie: {
    slug: 'ordindeprotectie',
    category: 'Penal',
    badge: 'ORDIN DE PROTECȚIE',
    image:
      'https://images.unsplash.com/photo-1521790361543-f645cf042ec4?w=800&h=600&fit=crop',
    shortDescription:
      'Soluții legale rapide când ești în pericol sau când copiii sunt expuși la violență.',
    hero: {
      title: 'Pachetul Ordin de Protecție și Plângere Penală pentru Violențe',
      subtitle:
        'Există soluții legale rapide când ești în pericol sau când copiii sunt expuși la violență. Nu trebuie să aștepți și nu trebuie să treci singur prin asta.',
    },
    awareness: {
      title:
        'Ești în pericol sau copiii tăi sunt expuși la violențe. Legea îți oferă protecție imediată.',
      paragraphs: [
        'Există violențe fizice, psihice sau verbale în familia ta. Poate ți s-a spus că dacă nu ai vânătăi vizibile, nu poți face nimic. Că nimeni nu te va crede. Că el va câștiga oricum pentru că are bani sau relații.',
        'Acestea nu sunt adevărate.',
        'Legea română prevede instrumente legale rapide. Ordinul de protecție poate fi emis în câteva zile, obligând agresorul să plece din locuință și să nu se apropie de tine sau de copii. Plângerea penală declanșează cercetarea penală și poate duce la condamnare. Violența psihologică, amenințările și controlul financiar, adică situația în care soțul controlează toți banii și nu îți dă acces la resurse, sunt și ele recunoscute de lege, nu doar violența fizică.',
      ],
    },
    benefits: {
      title:
        'Ce obții lucrând cu mine pe dosarul de protecție și plângere penală',
      items: [
        'Obții ordinul de protecție rapid, care obligă agresorul să plece din locuință și să nu se mai apropie de tine sau de copii, cu consecințe penale dacă nu respectă.',
        'Depunem plângerea penală pentru violență în familie, inclusiv pentru violență psihologică și amenințări, nu doar pentru violență fizică vizibilă.',
        'Îți explic ce probe sunt necesare și cum să le aduni în siguranță, fără să te expui la riscuri suplimentare sau la reacții imprevizibile din partea agresorului.',
        'Dacă există copii implicați, putem solicita concomitent și măsuri provizorii privind custodia și programul de vizită, ca să fie protejați și ei.',
        'Mă ocup de tot: redactarea cererii, depunerea la instanță sau poliție, urmărirea termenelor și comunicarea cu toate organele competente.',
        'Confidențialitate completă. Totul se gestionează cu discreție maximă față de orice persoană din exterior.',
      ],
    },
    howWeHelp: {
      title: 'Ce clarificăm împreună înainte de a porni dosarul',
      intro:
        'Una dintre cele mai frecvente convingeri greșite cu care vin clienții este că nu pot face nimic dacă nu au leziuni fizice vizibile sau dacă agresorul are mai multă putere financiară. Legea spune altceva. Înainte de orice dosar, îți explicăm exact ce forme de violență sunt recunoscute legal, ce instrumente ai la dispoziție și ce poți obține concret pentru tine și pentru copiii tăi.',
      items: [
        'Ce forme de violență sunt recunoscute de lege și dau dreptul la ordin de protecție',
        'Cât durează să obții ordinul de protecție și care sunt etapele procedurii',
        'Ce se întâmplă dacă agresorul nu respectă ordinul de protecție',
        'Dacă poți cere custodia copiilor în același timp cu ordinul de protecție',
        'Ce probe sunt necesare și cum le aduni în siguranță fără să te expui',
        'Dacă înregistrările audio sau video pot fi folosite ca probe în dosar',
        'Ce este violența psihologică și controlul financiar din punct de vedere legal',
        'Dacă trebuie să fii în aceeași localitate cu agresorul ca să ceri protecție',
        'Cum te protejezi în perioada dintre depunerea cererii și emiterea ordinului',
        'Ce se întâmplă cu dosarul penal după ce obții ordinul de protecție',
      ],
    },
    process: {
      title: 'Cum lucrăm împreună de la primul pas până la final',
      steps: [
        {
          title: 'Pasul 1 — Sesiunea de Claritate',
          text: 'Îmi prezinți situația în deplină confidențialitate. Nu există judecată și nu există informații pe care nu le pot auzi. Stabilim împreună ce instrumente legale sunt disponibile pentru cazul tău, în ce ordine le folosim și care este cel mai sigur și mai rapid drum pentru tine și pentru copiii tăi.',
        },
        {
          title: 'Pasul 2 — Adunarea probelor',
          text: 'Îți explic ce probe sunt relevante din punct de vedere legal: certificate medicale, mesaje, email-uri, martori, înregistrări audio sau video, capturi de ecran. Îți spun cum să le aduni și cum să le păstrezi în siguranță, fără să te expui la reacții periculoase.',
        },
        {
          title: 'Pasul 3 — Depunerea cererii de ordin de protecție',
          text: 'Redactez cererea și o depun la instanță. Ordinul de protecție provizoriu poate fi emis în câteva zile, chiar înainte de judecata propriu-zisă. Aceasta este o procedură de urgență, nu una obișnuită.',
        },
        {
          title: 'Pasul 4 — Plângerea penală',
          text: 'Depunem concomitent sau ulterior, în funcție de situație, plângerea penală la parchet sau poliție, cu toate probele disponibile la acel moment.',
        },
        {
          title: 'Pasul 5 — Reprezentare și urmărire completă',
          text: 'Mă prezint la toate termenele pentru ambele dosare. Ești informat la fiecare pas. Nimic nu se întâmplă fără să știi și fără să fii pregătit.',
        },
      ],
    },
    forWhom: {
      forTitle: 'Pachetul de Protecție este pentru tine dacă',
      forItems: [
        'Ești victima violențelor fizice, psihice, verbale sau a controlului financiar din partea partenerului sau fostului partener și vrei să pui capăt acestei situații pe cale legală.',
        'Copiii tăi sunt martori sau victime ale acestor violențe și vrei să îi protejezi printr-o decizie a instanței, nu doar printr-o promisiune verbală.',
        'Vrei să știi ce pași legali poți face chiar acum, fără să aștepți să se înrăutățească lucrurile, și fără să treci singur prin acest proces.',
      ],
      notForTitle: 'Pachetul de Protecție nu este pentru tine dacă',
      notForItems: [
        'Nu există violențe sau comportamente abuzive reale și cauți un instrument legal pentru alte scopuri, deoarece ordinul de protecție necesită o bază factuală clară și dovedibilă.',
        'Vrei să folosești ordinul de protecție ca tactică în conflictele legate de custodie sau partaj, fără ca situația de violență să fie reală, ceea ce nu este posibil din punct de vedere legal și etic.',
        'Situația este dificilă emoțional, dar nu implică violență sau amenințări în sensul legii, caz în care există alte instrumente juridice mai potrivite pentru situația ta.',
      ],
    },
    included: {
      title:
        'Hai să vedem ce include Pachetul Ordin de Protecție și Plângere Penală',
      items: [
        'Sesiunea inițială de consultanță în deplină confidențialitate (150 EUR + TVA, aplicați integral la pachet dacă decidem să continuăm)',
        'Clarificări complete despre ce forme de violență sunt recunoscute legal, ce instrumente ai și ce poți obține concret',
        'Ghidaj pentru adunarea și păstrarea în siguranță a probelor necesare',
        'Redactarea cererii de ordin de protecție și depunerea la instanță',
        'Reprezentare la termenul de judecată pentru ordin de protecție',
        'Redactarea plângerii penale și depunerea la parchet sau poliție',
        'Urmărirea ambelor dosare și comunicare permanentă la fiecare pas important',
      ],
      ctaText: 'Rezervă Sesiunea de Claritate și află prețul pachetului',
      ctaSmall: CTA_SMALL,
    },
    testimonials: {
      title: 'Ce spun clienții care au ales protecția legală',
    },
    faq: {
      title:
        'Întrebări frecvente despre ordinul de protecție și plângerea penală',
      items: [
        {
          q: 'Am nevoie de vânătăi sau leziuni fizice ca să pot obține ordin de protecție?',
          a: 'Nu. Violența psihologică, amenințările, controlul financiar și hărțuirea sunt recunoscute de lege și pot constitui baza unui ordin de protecție. Nu este necesar să existe violență fizică vizibilă sau certificată de medic. Analizăm ce probe ai disponibile în sesiunea de consultanță.',
        },
        {
          q: 'Cât durează să obțin ordinul de protecție?',
          a: 'Ordinul de protecție provizoriu poate fi emis de instanță în câteva zile de la depunerea cererii, deoarece este o procedură de urgență. Ordinul definitiv se obține după judecata propriu-zisă. Îți explic exact termenele pentru situația ta în sesiunea de consultanță.',
        },
        {
          q: 'Ce se întâmplă dacă agresorul nu respectă ordinul de protecție?',
          a: 'Nerespectarea ordinului de protecție constituie infracțiune penală și se pedepsește ca atare. Poți sesiza imediat poliția, care are obligația legală să intervină. Nu ești singur în această situație și există un mecanism clar de reacție.',
        },
        {
          q: 'Pot cere și custodia copiilor în același timp?',
          a: 'Da. Odată cu cererea de ordin de protecție sau prin proceduri separate, putem solicita măsuri provizorii privind custodia copiilor și locuința lor. Strategia exactă se stabilește în sesiunea de consultanță, în funcție de situația ta.',
        },
        {
          q: 'Trebuie să fiu în aceeași localitate cu agresorul ca să iau ordin de protecție?',
          a: 'Nu. Cererea se poate depune chiar dacă nu locuiți în aceeași localitate. Important este ca instanța competentă să fie corect identificată, lucru de care mă ocup eu. Poți fi în orice localitate din țară sau chiar în altă țară și tot putem acționa.',
        },
      ],
    },
  },

  divortsiordonanta: {
    slug: 'divortsiordonanta',
    category: 'Divorț & Partaj',
    badge: 'DIVORȚ + ORDONANȚĂ',
    image:
      'https://images.unsplash.com/photo-1436450412740-6b988f486c6b?w=800&h=600&fit=crop',
    shortDescription:
      'Pornești divorțul și obții imediat o decizie provizorie privind locuința sau copiii.',
    hero: {
      title: 'Pachetul Complet Divorț și Ordonanță Președințială',
      subtitle:
        'Pornești divorțul și obții imediat o decizie provizorie a instanței privind locuința sau copiii, fără să aștepți finalizarea procesului de divorț.',
    },
    awareness: {
      title:
        'Nu poți aștepta un an sau doi ca să afli cine rămâne în casă sau la cine stau copiii.',
      paragraphs: [
        'Ești în plin divorț și situația din casă este insuportabilă. Tu și soțul locuiți în continuare împreună, dar relația s-a rupt complet. Sau ai plecat și acum el stă în casa pe care o dețineți amândoi.',
        'Procesul de divorț poate dura mult. Dar nu trebuie să aștepți finalul lui ca să obții o soluție provizorie.',
        'Ordonanța președințială este un instrument legal rapid prin care instanța ia o decizie urgentă acum: cine folosește locuința familiei până la finalizarea divorțului, la cine stau copiii provizoriu și ce program de vizită se aplică în această perioadă. Nu peste un an. Acum.',
      ],
    },
    benefits: {
      title: 'Ce obții cu Pachetul Complet Divorț și Ordonanță Președințială',
      items: [
        'Obții rapid o decizie provizorie a instanței privind locuința și copiii, fără să aștepți finalizarea divorțului, care poate dura un an sau mai mult.',
        'Dacă soțul refuză să plece din casă, instanța poate dispune prin ordonanță ca el să elibereze locuința, chiar dacă sunteți amândoi proprietari.',
        'Stabilim provizoriu la cine locuiesc copiii și ce program de vizită se aplică în perioada procesului de divorț, nu la finalul lui.',
        'Pornim concomitent și dosarul de divorț, astfel încât totul avansează în același timp, nu pe rând, cu pauze între ele.',
        'Ai o singură strategie coerentă pentru ambele proceduri, nu două abordări separate care se pot contrazice în fața judecătorului.',
      ],
    },
    howWeHelp: {
      title: 'Ce clarificăm împreună înainte de a porni dosarul',
      intro:
        'Situațiile urgente cer răspunsuri clare și rapide. Înainte de a porni orice procedură, îți explicăm exact ce este posibil, ce nu este posibil și care este cel mai bun drum pentru situația ta concretă. Nu primești un răspuns standard. Primești o analiză a cazului tău.',
      items: [
        'Ce este ordonanța președințială și în ce situații se poate obține',
        'Care sunt condițiile legale pentru a cere scoaterea soțului din locuință',
        'Ce probe sunt necesare pentru a demonstra urgența în fața instanței',
        'Cum se stabilesc provizoriu custodia și programul de vizită al copiilor',
        'Cât durează procedura de urgență și la ce termene să te aștepți',
        'Ce se întâmplă cu decizia provizorie după ce divorțul este finalizat',
        'Cum merge dosarul de divorț în paralel cu ordonanța',
        'Ce acte sunt necesare pentru depunerea ambelor cereri',
        'Care sunt costurile reale ale acestei combinații de proceduri',
        'Ce opțiuni ai dacă soțul nu respectă decizia instanței',
      ],
    },
    process: {
      title: 'Cum lucrăm împreună de la primul pas până la final',
      steps: [
        {
          title: 'Pasul 1 — Sesiunea de Claritate',
          text: 'Îmi prezinți situația: cum stă locuința, cine este proprietar, unde locuiesc copiii și ce dorești să obții urgent. Stabilim împreună dacă ordonanța președințială este calea potrivită pentru cazul tău și ce probe sunt necesare pentru a demonstra urgența.',
        },
        {
          title: 'Pasul 2 — Redactarea dosarelor',
          text: 'Redactez concomitent cererea de ordonanță președințială, care este o procedură urgentă cu termen rapid, și acțiunea de divorț cu toate cererile accesorii: custodie, pensie de întreținere, program de vizită și atribuirea locuinței.',
        },
        {
          title: 'Pasul 3 — Procedura de urgență',
          text: 'Cererea de ordonanță se judecă rapid, de obicei în câteva săptămâni față de un an cât poate dura divorțul. Instanța poate dispune măsuri provizorii privind locuința și copiii chiar în această etapă.',
        },
        {
          title: 'Pasul 4 — Divorțul continuă în paralel',
          text: 'În același timp, dosarul de divorț merge pe procedura obișnuită. La final, hotărârea de divorț înlocuiește măsurile provizorii cu soluții definitive și permanente.',
        },
        {
          title: 'Pasul 5 — Reprezentare completă până la final',
          text: 'Mă prezint la fiecare termen din ambele dosare. Ești informat după fiecare dată despre ce s-a decis și ce urmează. Nimic nu se întâmplă fără să știi.',
        },
      ],
    },
    forWhom: {
      forTitle:
        'Pachetul Complet Divorț și Ordonanță Președințială este pentru tine dacă',
      forItems: [
        'Ești în situație urgentă: locuiești în continuare cu soțul deși relația este imposibilă, sau ai plecat și vrei să redobândești accesul la casă și la copii.',
        'Vrei să stabilești rapid la cine locuiesc copiii și ce program de vizită se aplică acum, nu după un an de așteptare.',
        'Îți dorești să pornești divorțul și să obții concomitent o soluție provizorie, fără să stai luni de zile fără nicio decizie.',
      ],
      notForTitle:
        'Pachetul Complet Divorț și Ordonanță Președințială nu este pentru tine dacă',
      notForItems: [
        'Situația locuinței este deja clarificată, copiii au un program stabil și nu există urgență care să nu suporte amânare.',
        'Nu sunt îndeplinite condițiile legale pentru ordonanța președințială, lucru pe care îl stabilim împreună în sesiunea de consultanță înainte de orice decizie.',
        'Preferi să pornești mai întâi divorțul și să vezi cum evoluează situația înainte de a adăuga o a doua procedură urgentă.',
      ],
    },
    included: {
      title:
        'Hai să vedem ce include Pachetul Complet Divorț și Ordonanță Președințială',
      items: [
        'Sesiunea inițială de consultanță și analiza situației urgente (150 EUR + TVA, aplicați integral la pachet dacă decidem să continuăm)',
        'Clarificări complete despre procedură, condiții legale, probe necesare și termene înainte de a porni orice dosar',
        'Redactarea cererii de ordonanță președințială și depunerea la instanță',
        'Reprezentare la termenul de urgență pentru ordonanță',
        'Redactarea acțiunii de divorț cu toate cererile accesorii',
        'Reprezentare la toate termenele dosarului de divorț',
        'Comunicare permanentă pe email și WhatsApp pe tot parcursul ambelor proceduri',
      ],
      ctaText: 'Rezervă Sesiunea de Claritate și află prețul pachetului',
      ctaSmall: CTA_SMALL,
    },
    testimonials: {
      title: 'Ce spun clienții care au ales această combinație de proceduri',
    },
    faq: {
      title:
        'Întrebări frecvente despre Pachetul Divorț și Ordonanță Președințială',
      items: [
        {
          q: 'Ce este ordonanța președințială și în ce condiții se poate obține?',
          a: 'Ordonanța președințială este o procedură urgentă prin care instanța ia o decizie provizorie pentru situații care nu suportă amânare. Condiția esențială este urgența reală și dovedită. Nu orice situație dificilă se califică. Analizăm împreună dacă cazul tău îndeplinește condițiile legale înainte de a porni orice procedură.',
        },
        {
          q: 'Cât durează procedura de urgență?',
          a: 'Mult mai puțin decât divorțul obișnuit. Un termen de urgență se poate obține în câteva săptămâni. Decizia este provizorie și poate fi modificată sau confirmată prin hotărârea finală de divorț.',
        },
        {
          q: 'Dacă obțin ordonanța, soțul este obligat să plece din casă?',
          a: 'Dacă instanța dispune prin ordonanță că tu vei folosi locuința, soțul este obligat legal să se conformeze. Nerespectarea unei decizii a instanței are consecințe legale concrete pe care le discutăm în sesiunea de consultanță.',
        },
        {
          q: 'Decizia provizorie privind copiii poate fi schimbată la divorț?',
          a: 'Da. Ordonanța stabilește soluții provizorii valabile pe durata procesului. La finalizarea divorțului, judecătorul poate menține sau modifica aceste soluții în funcție de probele prezentate pe tot parcursul procesului.',
        },
        {
          q: 'Pot cere ordonanța dacă am deja dosar de divorț pe rol?',
          a: 'Da. Ordonanța președințială poate fi cerută și în paralel cu un dosar de divorț deja pornit, dacă apare o situație urgentă care nu poate aștepta termenele obișnuite ale divorțului. Analizăm situația ta concretă în sesiunea de consultanță.',
        },
      ],
    },
  },

  divortsilocuinta: {
    slug: 'divortsilocuinta',
    category: 'Divorț & Partaj',
    badge: 'DIVORȚ + LOCUINȚĂ',
    image:
      'https://images.unsplash.com/photo-1518780664697-55e3ad937233?w=800&h=600&fit=crop',
    shortDescription:
      'Divorțezi și ceri instanței să stabilească că tu rămâi în casa familiei cu copiii.',
    hero: {
      title: 'Pachetul Complet Divorț și Atribuirea Locuinței Comune',
      subtitle:
        'Divorțezi și ceri instanței să stabilească că tu rămâi în casa familiei cu copiii, chiar dacă ești amândoi proprietari și chiar dacă soțul nu vrea să plece.',
    },
    awareness: {
      title:
        'Mulți cred că trebuie să plece din casă atunci când divorțează. Legea spune altceva.',
      paragraphs: [
        'Ești mamă, locuiești cu copiii în casa familiei și îți este teamă că va trebui să pleci. Poate soțul îți spune că el este cel care rămâne, pentru că el a adus mai mulți bani sau pentru că el este proprietar. Poate crezi că nu ai nicio șansă să rămâi.',
        'Legea română permite instanței să stabilească, în cadrul dosarului de divorț, care dintre soți va folosi locuința comună după divorț. Dacă copiii locuiesc cu tine și este în interesul lor să rămână în acea locuință, instanța poate dispune ca tu să folosești casa, chiar dacă soțul este proprietar sau coproprietar.',
        'Nu ai nevoie de un dosar de partaj deschis pentru asta. Cererea se face direct în dosarul de divorț, de la bun început.',
      ],
    },
    benefits: {
      title:
        'Ce obții cu Pachetul Complet Divorț și Atribuirea Locuinței Comune',
      items: [
        'Ceri instanței, chiar în cadrul dosarului de divorț, să stabilească că tu rămâi în locuința familiei cu copiii, până la soluționarea partajului.',
        'Nu ai nevoie de un dosar de partaj separat. Cererea se introduce direct în acțiunea de divorț, fără proceduri suplimentare.',
        'Dacă există copii minori care locuiesc cu tine, instanța prioritizează interesul copilului atunci când decide cine folosește locuința, nu cine a contribuit mai mult financiar.',
        'Îți clarific din sesiunea de consultanță ce șanse reale ai pentru atribuirea locuinței, în funcție de situația ta concretă, și ce probe sunt necesare pentru a câștiga.',
        'Strategia pentru atribuirea locuinței, custodia, pensia de întreținere și divorțul merg împreună, cu o singură abordare coerentă în fața judecătorului.',
      ],
    },
    howWeHelp: {
      title: 'Ce clarificăm împreună înainte de a porni dosarul',
      intro:
        'Teama că vei pierde casa în care locuiești cu copiii este una dintre cele mai frecvente frici cu care vin clienții la mine. De cele mai multe ori, această teamă nu corespunde cu ceea ce spune legea. Înainte de orice dosar, îți explicăm exact ce drepturi ai, ce șanse reale există și ce este necesar pentru a le obține.',
      items: [
        'Ce înseamnă atribuirea locuinței comune și cum funcționează în lege',
        'În ce condiții instanța poate dispune ca tu să rămâi în casă chiar dacă soțul este proprietar',
        'Ce rol joacă interesul copilului în decizia judecătorului privind locuința',
        'Ce probe sunt necesare pentru a susține cererea de atribuire a locuinței',
        'Dacă poți cere atribuirea locuinței fără să ai un dosar de partaj deschis',
        'Ce se întâmplă cu decizia de atribuire după ce se finalizează partajul',
        'Cum merge dosarul de divorț în paralel cu cererea de atribuire',
        'Ce poate face soțul dacă nu este de acord cu decizia instanței',
        'Cât timp ești protejată prin această decizie și ce urmează după',
        'Ce acte sunt necesare pentru depunerea dosarului complet',
      ],
    },
    process: {
      title: 'Cum lucrăm împreună de la primul pas până la final',
      steps: [
        {
          title: 'Pasul 1 — Sesiunea de Claritate',
          text: 'Îmi prezinți situația: cine este proprietar pe locuință, cu cine locuiesc copiii, care este atitudinea soțului și ce dorești să obții. Îți spun clar ce șanse ai și ce probe sunt necesare înainte de a porni orice procedură.',
        },
        {
          title: 'Pasul 2 — Redactarea dosarului complet',
          text: 'Redactez acțiunea de divorț care include toate cererile dintr-o singură dată: custodia copiilor, pensia de întreținere, programul de vizită și cererea de atribuire a locuinței comune. Îți trimit documentul înainte de depunere ca să fii confortabilă cu fiecare cuvânt.',
        },
        {
          title: 'Pasul 3 — Depunerea și procedura prealabilă',
          text: 'Dosarul este depus la instanță și comunicat celeilalte părți, adică soțului tău. Acesta are dreptul să răspundă cu propriile cereri. Răspundem împreună la tot ce depune.',
        },
        {
          title: 'Pasul 4 — Administrarea probelor pentru atribuirea locuinței',
          text: 'Identificăm și pregătim probele care susțin cererea ta: situația concretă a copiilor, școala din zonă, legăturile lor cu locuința actuală și orice altceva care demonstrează că atribuirea este în interesul lor. Probele corecte fac diferența.',
        },
        {
          title: 'Pasul 5 — Reprezentare până la hotărârea finală',
          text: 'Mă prezint la fiecare termen de judecată și urmăresc toate aspectele dosarului până la pronunțarea hotărârii. Ești informată după fiecare termen despre ce s-a decis și ce urmează.',
        },
      ],
    },
    forWhom: {
      forTitle:
        'Pachetul Complet Divorț și Atribuirea Locuinței este pentru tine dacă',
      forItems: [
        'Locuiești cu copiii în casa familiei și vrei să rămâi acolo după divorț, fără să fii nevoită să pleci în chirie în timp ce soțul stă în casă.',
        'Îți este teamă că soțul te va obliga să pleci sau că va reuși să rămână el în casă pe motiv că este proprietar sau că a contribuit mai mult financiar.',
        'Vrei să înțelegi ce șanse reale ai pentru atribuirea locuinței și ce probe concrete îți susțin cererea în fața judecătorului.',
      ],
      notForTitle:
        'Pachetul Complet Divorț și Atribuirea Locuinței nu este pentru tine dacă',
      notForItems: [
        'Nu locuiești în momentul de față în locuința pentru care ceri atribuirea sau copiii nu locuiesc acolo, deoarece instanța analizează situația reală, nu situația dorită.',
        'Vrei să obții locuința nu pentru a locui în ea cu copiii, ci din alte motive. Judecătorul evaluează interesul copilului, nu preferința sau avantajul financiar al părintelui.',
        'Situația locuinței este deja clarificată și există un alt aranjament stabil în vigoare cu care ambele părți sunt de acord.',
      ],
    },
    included: {
      title:
        'Hai să vedem ce include Pachetul Complet Divorț și Atribuirea Locuinței Comune',
      items: [
        'Sesiunea inițială de consultanță și analiza șanselor reale pentru atribuirea locuinței (150 EUR + TVA, aplicați integral la pachet dacă decidem să continuăm)',
        'Clarificări complete despre procedură, condiții legale, probe necesare și ce poate face soțul în această situație',
        'Redactarea acțiunii de divorț cu cererea de atribuire a locuinței, custodie, pensie de întreținere și program de vizită',
        'Identificarea și administrarea probelor necesare pentru cererea de atribuire',
        'Reprezentare la fiecare termen de judecată',
        'Comunicare permanentă pe email și WhatsApp pe tot parcursul dosarului',
      ],
      ctaText: 'Rezervă Sesiunea de Claritate și află prețul pachetului',
      ctaSmall: CTA_SMALL,
    },
    testimonials: {
      title: 'Ce spun clienții care au rămas în locuința familiei',
    },
    faq: {
      title: 'Întrebări frecvente despre atribuirea locuinței comune',
      items: [
        {
          q: 'Pot cere atribuirea locuinței chiar dacă aceasta este trecută pe numele soțului?',
          a: 'Da. Instanța poate decide că tu folosești locuința chiar dacă soțul este proprietar exclusiv sau coproprietar. Criteriul principal nu este cine este pe acte, ci interesul copilului și necesitatea de a-i asigura stabilitate în mediul în care deja locuiește.',
        },
        {
          q: 'Am nevoie de un dosar de partaj separat ca să cer atribuirea locuinței?',
          a: 'Nu. Cererea de atribuire a locuinței se introduce direct în acțiunea de divorț și instanța o poate soluționa chiar dacă nu există dosar de partaj deschis. Sunt două lucruri diferite din punct de vedere legal.',
        },
        {
          q: 'Dacă rămân în casă, soțul poate să vină oricând?',
          a: 'Nu. Dacă instanța îți atribuie locuința, soțul nu are dreptul de a accesa locuința fără acordul tău pe perioada stabilită. Dacă încearcă să intre fără permisiunea ta, poate fi vorba de tulburare de posesie sau altă faptă penală, în funcție de circumstanțe.',
        },
        {
          q: 'Cât timp pot rămâne în casă prin această decizie?',
          a: 'Atribuirea locuinței prin divorț este provizorie și este valabilă până la soluționarea partajului. Odată finalizat partajul, situația se rezolvă definitiv. Strategia pentru continuarea pe termen lung o stabilim împreună în sesiunea de consultanță.',
        },
        {
          q: 'Ce probe am nevoie ca să câștig cererea de atribuire a locuinței?',
          a: 'Probe care arată că tu și copiii locuiți efectiv acolo, că copiii merg la școala din zonă, că au relații stabile în acea comunitate și că mutarea ar afecta negativ viața lor. Analizăm împreună ce probe ai disponibile și ce mai trebuie să pregătim înainte de depunerea dosarului.',
        },
      ],
    },
  },
}

export const PACKAGE_ORDER = [
  'divortsipartaj',
  'divort',
  'partaj',
  'divortsiordonanta',
  'divortsilocuinta',
  'mosteniri',
  'ordindeprotectie',
  'pensiesiprogram',
  'pachetexecutari',
]

export const PACKAGE_LIST: OfferPackage[] = PACKAGE_ORDER.map(
  (s) => PACKAGES[s],
)
